// The review card (Session AUB, Item 3): one conversation, read in full, with its ready reply and every review action.
//
// THE SEND IS THE ENQUIRY QUEUE'S ONE PERSON-PRESSED PATH (Session ATW). "Approve and send" first makes the ready reply the
// conversation's reviewed draft (POST /api/mail/messages/:id/prepare-send, which sends nothing), then opens the SAME
// branded confirm the enquiry pages use (EnquirySendConfirmModal), which asks the server who the reply goes to and names
// every recipient and the From address. Only the confirm's own Send button sends, with the recipient it named
// (expectedTo), so a reply never goes to someone the person was not shown. This is the human review step the auto-send
// rule (AHR) and D-MAIL-1 require; nothing here sends on its own.
//
// Every action has a labelled button (Item 8). The keyboard shortcuts in MailLaneView call the same functions through
// the handle below, never a second path.
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../../../lib/api";
import type { MailAssignee, MailDismissReason, MailListRow, MailMessageDetail, MailTarget } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import {
  DISMISS_NOTE_MAX,
  DISMISS_REASON_LABELS,
  DISMISS_REASONS,
  dismissalRefusal,
  fmtLondonDateTime,
  londonLocalInputToIso,
  SNOOZE_PRESET_LABELS,
  snoozeUntil,
  type SnoozePreset,
} from "../../../lib/mailWorkspace";
import { ModalShell, MODAL_ACTIONS, MODAL_PRIMARY_BTN, MODAL_SECONDARY_BTN } from "../../../components/ModalShell";
import { StatusPill } from "../../../components/StatusPill";
import { EnquirySendConfirmModal } from "../EnquirySendConfirmModal";
import { DraftComposer } from "../EmailEnginePage";
import { MailContextPanel } from "./MailContextPanel";
import { fmtBytes, matchText, senderText, templateMissParts } from "./mailUi";
import { MailLolerRebill } from "./MailLolerRebill";
import { Button } from "../../../components/Button";
import { ConfirmButton } from "../../../components/ConfirmButton";

export interface MailReviewCardHandle {
  send: () => void;
  edit: () => void;
  assignTo: (index: number) => void;
  openAssign: () => void;
  openSnooze: () => void;
  openDismiss: () => void;
  openGmail: () => void;
  focus: () => void;
}

/** What the card tells its list after an action that may move the conversation out of its lane. */
export interface MailActionDone {
  action: "send" | "dismiss" | "snooze" | "wake" | "restore" | "assign" | "match" | "edit" | "draft" | "bring_back";
  notice?: string;
}

const ACTION_BTN =
  "inline-flex items-center justify-center rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-strong transition hover:bg-ele-inset disabled:opacity-50 max-sm:min-h-11 max-sm:flex-1";
const LABEL = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const FIELD = "w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";

type Modal = null | "assign" | "snooze" | "dismiss";

export const MailReviewCard = forwardRef<
  MailReviewCardHandle,
  {
    row: MailListRow;
    assignees: MailAssignee[];
    /** Laptop layout: the context sits beside or under the message; on a phone it is an in-card tab. */
    wide: boolean;
    onBack?: () => void;
    /** Move on to the next conversation without doing anything. */
    onSkip: () => void;
    onDone: (done: MailActionDone) => void;
  }
>(function MailReviewCard({ row, assignees, wide, onBack, onSkip, onDone }, ref) {
  const [detail, setDetail] = useState<MailMessageDetail | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [view, setView] = useState<"message" | "context">("message");
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [sendTarget, setSendTarget] = useState<{ id: string; label: string } | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [modal, setModal] = useState<Modal>(null);
  const [busy, setBusy] = useState(false);
  const [rootEl, setRootEl] = useState<HTMLElement | null>(null);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setLoadState("loading");
      setLoadError(null);
      try {
        let message: MailMessageDetail | undefined;
        if (row.messageId) {
          message = (await api.mail.get(row.messageId)).message;
        } else if (row.enquiryId) {
          const res = await api.mail.getEnquiry(row.enquiryId);
          if (res.redirectMessageId) message = (await api.mail.get(res.redirectMessageId)).message;
          else message = res.message;
        }
        if (!message) throw new Error("not found");
        setDetail(message);
        setLoadState("ready");
      } catch (err) {
        setLoadError(friendlyApiError(err, "This conversation could not be loaded."));
        setLoadState("error");
      }
    },
    [row.messageId, row.enquiryId],
  );

  useEffect(() => {
    setEditing(false);
    setActionError(null);
    setNotice(null);
    setView("message");
    setModal(null);
    setSendTarget(null);
    void load();
  }, [load]);

  const target: MailTarget = detail ? { messageId: detail.messageId, enquiryId: detail.enquiryId } : { messageId: row.messageId, enquiryId: row.enquiryId };
  const subject = (detail?.subject ?? row.subject)?.trim() || "(no subject)";
  const canSend = Boolean(detail?.send.allowed && detail.draft);
  const canEdit = canSend && Boolean(detail?.messageId);
  const gmailUrl = detail?.gmailUrl ?? row.gmailUrl;

  async function startSend() {
    if (!detail || !canSend || preparing) return;
    setActionError(null);
    setSendError(null);
    if (detail.kind === "enquiry" || !detail.messageId) {
      if (detail.enquiryId) setSendTarget({ id: detail.enquiryId, label: subject });
      return;
    }
    setPreparing(true);
    try {
      const res = await api.mail.prepareSend(detail.messageId);
      setSendTarget({ id: res.prepared.enquiryId, label: subject });
    } catch (err) {
      setActionError(friendlyApiError(err, "The reply could not be made ready to send. Please try again."));
      void load({ silent: true });
    } finally {
      setPreparing(false);
    }
  }

  async function confirmSend(recipients: Map<string, string>) {
    if (!sendTarget) return;
    const to = recipients.get(sendTarget.id);
    setSending(true);
    setSendError(null);
    try {
      const res = await api.enquiries.approveAndSend(sendTarget.id, to);
      setSendTarget(null);
      const note = res.afterSend?.note ? ` ${res.afterSend.note}` : "";
      onDone({ action: "send", notice: `Sent to ${to ?? "the person who wrote"}.${note}` });
    } catch (err) {
      if (err instanceof ApiError && (err.body?.error === "recipient_changed" || err.code === "recipient_changed")) {
        setSendTarget(null);
        setActionError(
          `Not sent: ${friendlyApiError(err, "the conversation has changed since the confirm was opened, so the reply would now reach someone else.")} The card has been reloaded; check it and send again.`,
        );
        void load({ silent: true });
      } else {
        setSendError(friendlyApiError(err, "The reply could not be sent. Please try again."));
      }
    } finally {
      setSending(false);
    }
  }

  function startEdit() {
    if (!detail?.draft || !canEdit) return;
    setEditText(detail.draft.text);
    setEditing(true);
    setActionError(null);
  }

  // Session AUB review: a message with no ready draft (needs a person) can still be answered from here in the person's own
  // words. Saving makes them the reply (prepare-send); nothing is sent until Approve and send and its confirm.
  const canWrite = Boolean(detail && !detail.draft && detail.send.code === "no_draft" && detail.messageId && detail.kind === "message");
  function startWrite() {
    if (!canWrite) return;
    setEditText("");
    setEditing(true);
    setActionError(null);
  }

  async function saveEdit() {
    if (!detail?.messageId) return;
    setSaving(true);
    setActionError(null);
    try {
      await api.mail.prepareSend(detail.messageId, editText);
      setEditing(false);
      setNotice("Your edit is saved. Nothing has been sent.");
      await load({ silent: true });
      onDone({ action: "edit" });
    } catch (err) {
      setActionError(friendlyApiError(err, "The edit could not be saved. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  async function assign(userId: string | null) {
    setBusy(true);
    setActionError(null);
    try {
      const res = await api.mail.assign(target, userId);
      setModal(null);
      setNotice(res.assignedToUserId ? `Assigned to ${res.assignedName ?? "them"}.` : "No longer assigned to anyone.");
      await load({ silent: true });
      onDone({ action: "assign" });
    } catch (err) {
      setActionError(friendlyApiError(err, "It could not be assigned. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function snooze(untilIso: string | null) {
    setBusy(true);
    setActionError(null);
    try {
      const res = await api.mail.snooze(target, untilIso);
      setModal(null);
      if (untilIso) onDone({ action: "snooze", notice: `"${subject}" is snoozed until ${fmtLondonDateTime(res.snoozedUntil ?? untilIso)}.` });
      else {
        setNotice("Woken: it is back in its lane.");
        await load({ silent: true });
        onDone({ action: "wake" });
      }
    } catch (err) {
      setActionError(friendlyApiError(err, "It could not be snoozed. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function dismiss(reason: MailDismissReason, note: string) {
    setBusy(true);
    setActionError(null);
    try {
      await api.mail.dismiss(target, reason, note.trim() || undefined);
      setModal(null);
      onDone({ action: "dismiss", notice: `"${subject}" was dismissed: ${DISMISS_REASON_LABELS[reason]}.` });
    } catch (err) {
      setActionError(friendlyApiError(err, "It could not be dismissed. Please try again."));
      setModal(null);
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    setBusy(true);
    setActionError(null);
    try {
      await api.mail.restore(target);
      setNotice("Restored: it is back in its lane.");
      await load({ silent: true });
      onDone({ action: "restore" });
    } catch (err) {
      setActionError(friendlyApiError(err, "It could not be restored. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  // Session SC4: the way back for a message the engine settled as needing no person (or filed). The two-step confirm sits on the button.
  async function bringBack() {
    if (!detail?.messageId) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await api.mail.bringBack(detail.messageId);
      setNotice(
        `Brought back: it is now in Needs you.${res.wasDismissed ? " It had been dismissed; that is cleared." : ""}${res.wasSnoozed ? " It had been snoozed; that is cleared." : ""}${res.fileLabelRemoved === false ? " Gmail would not take its filing label off, so remove that label in Gmail." : ""} Nothing was deleted or sent.`,
      );
      await load({ silent: true });
      onDone({ action: "bring_back" });
    } catch (err) {
      setActionError(friendlyApiError(err, "It could not be brought back. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  function openGmail() {
    window.open(gmailUrl, "_blank", "noopener,noreferrer");
  }

  useImperativeHandle(ref, () => ({
    send: () => void startSend(),
    edit: () => startEdit(),
    assignTo: (index: number) => {
      const a = assignees[index];
      if (a && !busy) void assign(a.id);
    },
    openAssign: () => setModal("assign"),
    openSnooze: () => setModal("snooze"),
    openDismiss: () => setModal("dismiss"),
    openGmail,
    focus: () => {
      rootEl?.focus();
      rootEl?.scrollIntoView?.({ block: "nearest" });
    },
  }));

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        {onBack && (
          <button type="button" onClick={onBack} className="mb-2 inline-flex items-center font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong max-sm:min-h-11">
            Back to list
          </button>
        )}
        <h2 className="font-heading text-xl text-ele-strong [overflow-wrap:anywhere]">{subject}</h2>
      </div>
      <div className="flex flex-wrap gap-1">
        {row.replyOverdue && <StatusPill word="Reply overdue (24h target)" tone="amber" />}
        {row.lateReply && <StatusPill word={row.lateReply} tone="amber" title="The reply goes out on a later working day than the email arrived, so the platform adds one fixed apology after the greeting" />}
        {detail?.stateLabel && <StatusPill word={detail.stateLabel} tone="grey" />}
      </div>
    </div>
  );

  if (loadState === "loading" && !detail) {
    return (
      <article aria-label={`Conversation: ${subject}`} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
        {header}
        <p role="status" className="mt-3 text-sm text-ele-body">
          Loading the conversation...
        </p>
      </article>
    );
  }
  if (loadState === "error" || !detail) {
    return (
      <article aria-label={`Conversation: ${subject}`} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
        {header}
        <div role="alert" className="mt-3 text-sm text-ele-strong">
          {loadError ?? "This conversation could not be loaded."}{" "}
          <button type="button" onClick={() => void load()} className="font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong max-sm:min-h-11">
            Retry
          </button>
        </div>
      </article>
    );
  }

  const d = detail;
  const draft = d.draft;
  const miss = draft?.kind === "free" ? templateMissParts(draft.templateMiss) : null;
  const match = matchText(d.match);

  const messageSection = (
    <div className="space-y-4">
      <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
        <Field label="From" value={senderText(d)} />
        {d.replyTo && d.replyTo.toLowerCase() !== (d.from ?? "").toLowerCase() && <Field label="Replies go to (Reply-To)" value={d.replyTo} />}
        <Field label="To" value={d.to.join(", ") || "-"} />
        {d.cc.length > 0 && <Field label="Cc" value={d.cc.join(", ")} />}
        <Field label="Received" value={fmtLondonDateTime(d.receivedAt)} />
        <Field label="Kind of email" value={d.categoryLabel ?? "Not yet sorted"} />
        <Field label="Customer" value={match.text} />
        {d.assigned && <Field label="Assigned to" value={d.assigned.name ?? "Someone"} />}
        {d.snoozedUntil && <Field label="Snoozed until" value={fmtLondonDateTime(d.snoozedUntil)} />}
      </dl>
      {d.outcomeReason && <p className="text-sm text-ele-body">Engine: {d.outcomeReason}</p>}
      {d.whyFlagged && <p className="text-sm text-ele-body">Why it was flagged: {d.whyFlagged}</p>}
      {d.readIssue && <p className="text-sm text-[var(--ele-amber-text)]">This message could not be read in full: {d.readIssue}</p>}
      {/* Session AUF (DL-3): an examining body's invoice or notice can be re-billed to a customer we arrange examinations for. */}
      {d.messageId && (d.category === "supplier_invoice" || d.category === "examiner_notice") && <MailLolerRebill inboundEmailId={d.messageId} />}
      {d.enquiry?.dismissal && (
        <p className="text-sm text-ele-body">
          Dismissed {fmtLondonDateTime(d.enquiry.dismissal.at)}: {d.enquiry.dismissal.reasonLabel}
          {d.enquiry.dismissal.note ? ` (${d.enquiry.dismissal.note})` : ""}.
        </p>
      )}

      <section aria-label="The message">
        <h3 className={LABEL}>The message</h3>
        {d.body ? (
          <>
            <div className="whitespace-pre-wrap break-words rounded-ele bg-ele-inset p-3 text-sm text-ele-strong" data-testid="mail-body">
              {d.body.text}
            </div>
            {d.body.truncated && <p className="mt-1 text-xs text-ele-body">This is the first part of a long message. Open it in Gmail for the rest.</p>}
          </>
        ) : (
          <p className="text-sm text-ele-body">
            Recorded from its headers only:{" "}
            <a href={gmailUrl} target="_blank" rel="noopener noreferrer" className="text-ele-blue underline-offset-2 hover:underline">
              open it in Gmail
            </a>{" "}
            to read it.
          </p>
        )}
        {d.attachments.length > 0 && (
          <ul className="mt-2 text-xs text-ele-body" aria-label="Attachments">
            {d.attachments.map((a, i) => (
              <li key={`${a.filename}-${i}`}>
                Attachment: {a.filename} ({fmtBytes(a.size)})
              </li>
            ))}
          </ul>
        )}
      </section>

      {d.checks.length > 0 && (
        <section aria-label="Checks the draft failed" className="rounded-ele border border-ele-hairline p-3">
          <h3 className={LABEL}>Checks the engine's draft failed</h3>
          <ul className="list-disc space-y-1 pl-5 text-sm text-ele-strong">
            {d.checks.map((c, i) => (
              <li key={i}>{c.detail || c.check}</li>
            ))}
          </ul>
        </section>
      )}

      <section aria-label="The reply">
        <h3 className={LABEL}>The reply</h3>
        {draft ? (
          <>
            <div className="mb-2 text-xs text-ele-body" data-testid="mail-draft-label">
              {draft.kind === "template" && draft.template ? (
                <p>
                  <span className="text-ele-strong">{draft.label ?? `From template ${draft.template.key}`}</span>
                  {draft.template.id && (
                    <>
                      {" · "}
                      <Link to={`/office/reply-templates/${encodeURIComponent(draft.template.id)}`} className="text-ele-blue underline-offset-2 hover:underline">
                        Edit template
                      </Link>
                    </>
                  )}
                </p>
              ) : (
                <p>
                  <span className="text-[var(--ele-amber-text)]">{draft.label ?? "Free draft"}</span>
                  {miss?.text ? <span>: {miss.text}</span> : null}
                  {miss && miss.missingSlots.length > 0 ? <span> (missing: {miss.missingSlots.join(", ")})</span> : null}
                </p>
              )}
              {draft.attachments.length > 0 && <p>Attached: {draft.attachments.map((a) => a.title).join(", ")}</p>}
            </div>
            {editing ? (
              <div>
                <label className="block">
                  <span className="sr-only">Edit the reply</span>
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={Math.min(24, Math.max(8, editText.split("\n").length + 2))}
                    className={`${FIELD} font-body`}
                    aria-label="Edit the reply"
                    autoFocus
                  />
                </label>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" className="max-sm:flex-1" onClick={() => void saveEdit()} disabled={saving || !editText.trim()}>
                    {saving ? "Saving..." : "Save the edit"}
                  </Button>
                  <button type="button" onClick={() => setEditing(false)} disabled={saving} className={ACTION_BTN}>
                    Cancel
                  </button>
                </div>
                <p className="mt-1 text-xs text-ele-body">Saving keeps your wording as the reply. It does not send it.</p>
              </div>
            ) : (
              <div className="whitespace-pre-wrap break-words rounded-ele border border-ele-hairline bg-ele-surface p-3 text-sm text-ele-strong" data-testid="mail-draft-text">
                {draft.text}
              </div>
            )}
            <p className="mt-1 text-xs text-ele-body opacity-80">Kind regards, then the Company signature are added when it is sent.</p>
          </>
        ) : editing && canWrite ? (
          <div>
            <label className="block">
              <span className="sr-only">Write the reply</span>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={10}
                className={`${FIELD} font-body`}
                aria-label="Write the reply"
                autoFocus
              />
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" className="max-sm:flex-1" onClick={() => void saveEdit()} disabled={saving || !editText.trim()}>
                {saving ? "Saving..." : "Save the reply"}
              </Button>
              <button type="button" onClick={() => setEditing(false)} disabled={saving} className={ACTION_BTN}>
                Cancel
              </button>
            </div>
            <p className="mt-1 text-xs text-ele-body">Write the reply only: Kind regards and the Company signature are added when it is sent. Saving does not send it.</p>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-ele-body">No reply is ready for this message.</p>
            {canWrite && (
              <button type="button" onClick={startWrite} className={ACTION_BTN}>
                Write the reply yourself
              </button>
            )}
          </div>
        )}
      </section>

      {!d.send.allowed && d.send.reason && (
        <p role="note" className="rounded-ele border border-[var(--ele-amber-text-deep)] bg-ele-inset px-3 py-2 text-sm text-ele-strong" data-testid="mail-send-refusal">
          {d.send.reason}
        </p>
      )}

      {!d.send.allowed && d.send.code === "no_draft" && d.messageId && d.kind === "message" && (
        <DraftComposer
          messageId={d.messageId}
          mode="draft"
          onCommitted={() => {
            void load({ silent: true });
            onDone({ action: "draft" });
          }}
        />
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {d.messageId ? (
          <Link to={`/office/email-engine/${encodeURIComponent(d.messageId)}`} className="text-ele-blue underline-offset-2 hover:underline max-sm:min-h-11 max-sm:inline-flex max-sm:items-center">
            Open the full record
          </Link>
        ) : null}
        {d.enquiryId && (
          <Link to={`/office/enquiries/${encodeURIComponent(d.enquiryId)}`} className="text-ele-blue underline-offset-2 hover:underline max-sm:min-h-11 max-sm:inline-flex max-sm:items-center">
            Open the enquiry record
          </Link>
        )}
        {d.workOrderProposalId && (
          <Link to="/office/mail?tab=work" className="text-ele-blue underline-offset-2 hover:underline max-sm:min-h-11 max-sm:inline-flex max-sm:items-center">
            Open the work order
          </Link>
        )}
      </div>
    </div>
  );

  const actions = (
    <div
      role="group"
      aria-label="Review actions"
      className="flex flex-wrap items-center gap-2 border-t border-ele-hairline pt-3 max-lg:sticky max-lg:bottom-0 max-lg:z-10 max-lg:bg-ele-surface max-lg:pb-2"
    >
      {canSend && (
        <Button size="sm" className="max-sm:w-full" onClick={() => void startSend()} disabled={preparing || sending || editing}>
          {preparing ? "Getting it ready..." : "Approve and send"}
        </Button>
      )}
      {canEdit && !editing && (
        <button type="button" onClick={startEdit} className={ACTION_BTN}>
          Edit
        </button>
      )}
      <button type="button" onClick={onSkip} className={ACTION_BTN}>
        Skip
      </button>
      <button type="button" onClick={() => setModal("assign")} disabled={busy} className={ACTION_BTN}>
        Assign
      </button>
      {d.facts.isSnoozed || d.snoozedUntil ? (
        <button type="button" onClick={() => void snooze(null)} disabled={busy} className={ACTION_BTN}>
          Wake now
        </button>
      ) : (
        <button type="button" onClick={() => setModal("snooze")} disabled={busy} className={ACTION_BTN}>
          Snooze
        </button>
      )}
      {d.kind === "message" && d.messageId && d.outcome === "no_reply_needed" && d.lane !== "work" && d.facts.isLatest && !d.facts.isAnswered && (
        <ConfirmButton
          onConfirm={() => void bringBack()}
          label="Bring back to the queue"
          confirmLabel="Press again: bring it back"
          busy={busy}
          busyLabel="Bringing it back..."
          className={ACTION_BTN}
          armedClassName="!text-ele-green font-semibold"
          title={`Puts this message in front of a person again.${d.facts.isDismissed ? " It was dismissed; that is cleared." : ""}${d.facts.isSnoozed || d.snoozedUntil ? " It was snoozed; that is cleared." : ""} Nothing is deleted or sent.`}
        />
      )}
      {d.facts.isDismissed ? (
        <button type="button" onClick={() => void restore()} disabled={busy} className={ACTION_BTN}>
          Restore
        </button>
      ) : (
        <button type="button" onClick={() => setModal("dismiss")} disabled={busy} className={ACTION_BTN}>
          Dismiss
        </button>
      )}
      <a href={gmailUrl} target="_blank" rel="noopener noreferrer" className={ACTION_BTN}>
        Open in Gmail
      </a>
    </div>
  );

  return (
    <article
      ref={setRootEl}
      tabIndex={-1}
      aria-label={`Conversation: ${subject}`}
      className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 outline-none"
      data-testid="mail-review-card"
    >
      {header}
      {notice && (
        <p role="status" className="mt-3 rounded-ele border border-ele-green bg-ele-field-inset px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}
      {actionError && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {actionError}
        </p>
      )}

      {wide ? (
        <div className="mt-4 grid gap-6 2xl:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
          <div className="min-w-0 space-y-4">
            {messageSection}
            {actions}
          </div>
          <div className="min-w-0 border-t border-ele-hairline pt-4 2xl:border-l 2xl:border-t-0 2xl:pl-6 2xl:pt-0">
            <MailContextPanel detail={d} onMatched={() => { void load({ silent: true }); onDone({ action: "match" }); }} />
          </div>
        </div>
      ) : (
        <>
          <div role="tablist" aria-label="Conversation sections" className="mt-3 flex gap-2 border-b border-ele-hairline pb-2">
            {(
              [
                { v: "message", l: "Message" },
                { v: "context", l: "Context" },
              ] as const
            ).map((t) => (
              <button
                key={t.v}
                type="button"
                role="tab"
                aria-selected={view === t.v}
                onClick={() => setView(t.v)}
                className={`min-h-11 flex-1 rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide ${
                  view === t.v ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body"
                }`}
              >
                {t.l}
              </button>
            ))}
          </div>
          <div className="mt-3 min-w-0">
            {view === "message" ? (
              <div className="space-y-4">
                {messageSection}
                {actions}
              </div>
            ) : (
              <MailContextPanel detail={d} onMatched={() => { void load({ silent: true }); onDone({ action: "match" }); }} />
            )}
          </div>
        </>
      )}

      {sendTarget && (
        <EnquirySendConfirmModal
          targets={[sendTarget]}
          busy={sending}
          error={sendError}
          onConfirm={(recipients) => void confirmSend(recipients)}
          onCancel={() => {
            setSendTarget(null);
            setSendError(null);
          }}
        />
      )}
      {modal === "assign" && <AssignModal assignees={assignees} current={d.assigned?.userId ?? null} busy={busy} onPick={(id) => void assign(id)} onClose={() => !busy && setModal(null)} />}
      {modal === "snooze" && <SnoozeModal busy={busy} onPick={(iso) => void snooze(iso)} onClose={() => !busy && setModal(null)} />}
      {modal === "dismiss" && <DismissModal busy={busy} subject={subject} onDismiss={(r, n) => void dismiss(r, n)} onClose={() => !busy && setModal(null)} />}
    </article>
  );
});

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className={LABEL}>{label}</dt>
      <dd className="break-words text-ele-strong [overflow-wrap:anywhere]">{value}</dd>
    </div>
  );
}

function AssignModal({
  assignees,
  current,
  busy,
  onPick,
  onClose,
}: {
  assignees: MailAssignee[];
  current: string | null;
  busy: boolean;
  onPick: (userId: string | null) => void;
  onClose: () => void;
}) {
  return (
    <ModalShell ariaLabel="Assign this conversation" onClose={onClose} closeOnBackdrop width="max-w-md" panelClassName="p-5">
      <h2 className="font-heading text-lg text-ele-strong">Assign this conversation</h2>
      <p className="mt-1 text-sm text-ele-body">The person you choose sees it under "Assigned to me". Nothing is sent to anyone.</p>
      {assignees.length === 0 ? (
        <p className="mt-3 text-sm text-ele-body">No one can be assigned yet.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {assignees.map((a, i) => (
            <li key={a.id}>
              <button
                type="button"
                disabled={busy}
                onClick={() => onPick(a.id)}
                className="flex min-h-11 w-full items-center justify-between rounded-ele border border-ele-purple-accent px-3 py-2 text-left text-sm text-ele-strong hover:bg-ele-inset disabled:opacity-50"
              >
                <span>
                  {a.name}
                  {a.me ? " (you)" : ""}
                  {current === a.id ? " · assigned now" : ""}
                </span>
                {i < 2 && <span className="font-menu text-xs text-ele-body">key {i + 1}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onClose} disabled={busy} className={MODAL_SECONDARY_BTN}>
          Cancel
        </button>
        {current && (
          <button type="button" onClick={() => onPick(null)} disabled={busy} className={MODAL_SECONDARY_BTN}>
            Unassign
          </button>
        )}
      </div>
    </ModalShell>
  );
}

function SnoozeModal({ busy, onPick, onClose }: { busy: boolean; onPick: (iso: string) => void; onClose: () => void }) {
  const [custom, setCustom] = useState("");
  const [error, setError] = useState<string | null>(null);
  const presets: SnoozePreset[] = ["tomorrow", "monday", "threeDays", "week"];
  function pickCustom() {
    const iso = londonLocalInputToIso(custom);
    if (!iso) {
      setError("Choose a date and time.");
      return;
    }
    if (new Date(iso).getTime() <= Date.now() + 60_000) {
      setError("Choose a time at least a minute from now.");
      return;
    }
    setError(null);
    onPick(iso);
  }
  return (
    <ModalShell ariaLabel="Snooze this conversation" onClose={onClose} closeOnBackdrop width="max-w-md" panelClassName="p-5">
      <h2 className="font-heading text-lg text-ele-strong">Snooze this conversation</h2>
      <p className="mt-1 text-sm text-ele-body">It leaves the list until the time you choose (London time), or sooner if a new message arrives.</p>
      <ul className="mt-3 space-y-2">
        {presets.map((p) => (
          <li key={p}>
            <button
              type="button"
              disabled={busy}
              onClick={() => onPick(snoozeUntil(p, new Date()))}
              className="min-h-11 w-full rounded-ele border border-ele-purple-accent px-3 py-2 text-left text-sm text-ele-strong hover:bg-ele-inset disabled:opacity-50"
            >
              {SNOOZE_PRESET_LABELS[p]}
            </button>
          </li>
        ))}
      </ul>
      <label className="mt-4 block">
        <span className={LABEL}>Or choose a date and time</span>
        <input type="datetime-local" value={custom} onChange={(e) => setCustom(e.target.value)} className={`${FIELD} max-sm:min-h-11`} aria-label="Snooze until (date and time)" />
      </label>
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onClose} disabled={busy} className={MODAL_SECONDARY_BTN}>
          Cancel
        </button>
        <button type="button" onClick={pickCustom} disabled={busy || !custom} className={MODAL_PRIMARY_BTN}>
          Snooze until then
        </button>
      </div>
    </ModalShell>
  );
}

function DismissModal({
  busy,
  subject,
  onDismiss,
  onClose,
}: {
  busy: boolean;
  subject: string;
  onDismiss: (reason: MailDismissReason, note: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<MailDismissReason>("no_action_needed");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  function confirm() {
    const refusal = dismissalRefusal(reason, note);
    if (refusal) {
      setError(refusal);
      return;
    }
    setError(null);
    onDismiss(reason, note);
  }
  return (
    <ModalShell ariaLabel="Dismiss this conversation" onClose={onClose} closeOnBackdrop width="max-w-md" panelClassName="p-5">
      <h2 className="font-heading text-lg text-ele-strong">Dismiss this conversation?</h2>
      <p className="mt-1 text-sm text-ele-body [overflow-wrap:anywhere]">
        "{subject}" leaves the list. Nothing is deleted and nothing is sent; a new message in the conversation brings it back, and it
        can be restored from All.
      </p>
      <fieldset className="mt-3">
        <legend className={LABEL}>Why</legend>
        <div className="space-y-1.5">
          {DISMISS_REASONS.map((r) => (
            <label key={r} className="flex min-h-11 items-center gap-2 text-sm text-ele-strong sm:min-h-0">
              <input type="radio" name="dismiss-reason" value={r} checked={reason === r} onChange={() => setReason(r)} />
              {DISMISS_REASON_LABELS[r]}
            </label>
          ))}
        </div>
      </fieldset>
      <label className="mt-3 block">
        <span className={LABEL}>{reason === "other" ? "Note (needed for Something else)" : "Note (optional)"}</span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={DISMISS_NOTE_MAX} className={FIELD} aria-label="Dismissal note" />
      </label>
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onClose} disabled={busy} className={MODAL_SECONDARY_BTN}>
          Cancel
        </button>
        <button type="button" onClick={confirm} disabled={busy} className={MODAL_PRIMARY_BTN}>
          {busy ? "Dismissing..." : "Dismiss it"}
        </button>
      </div>
    </ModalShell>
  );
}
